import type { PersistedTabContent } from 'apiConnector/tabs/tabs.model';
import type { SecondaryMarketCpOrderFormTabContent } from 'state/secondaryMarket/cpOrder/cpOrderForm/input/cpOrderInputForm.model';
import type { SecondaryMarketDlpOrderTabContent } from 'state/secondaryMarket/dlpOrder/form/dlpOrderForm.model';
import type { LimitOrderInputForm } from 'state/secondaryMarket/limit-order/form/input/inputForm.model';
import type { LiveInputForm } from 'state/secondaryMarket/live/form/input/inputForm.model';
import type { SecondaryMarketRtQuoteTabContent } from 'state/secondaryMarket/realtimeQuote/realtimeQuoteForm';
import type { Form, SecondaryMarketFormTabContent } from 'state/secondaryMarket/shared/form/form.model';

export type SecondaryMarketPersistedTabContent<Workspace, TabType, Content> = {
  workspace: Workspace;
  tabType: TabType;
} & Content;

export type SecondaryQuotesRtFormPersistedTabContent = SecondaryMarketPersistedTabContent<
  'secondaryMarketQuotes',
  'realtimeQuote',
  SecondaryMarketRtQuoteTabContent
>;

export type SecondaryQuotesCpOrderPersistedTabContent = SecondaryMarketPersistedTabContent<
  'secondaryMarketQuotes',
  'cpOrder',
  SecondaryMarketCpOrderFormTabContent
>;

export type SecondaryQuotesDlpOrderPersistedTabContent = SecondaryMarketPersistedTabContent<
  'secondaryMarketQuotes',
  'dlpOrder',
  SecondaryMarketDlpOrderTabContent
>;
export type SecondaryQuotesLiveFormPersistedTabContent = SecondaryMarketPersistedTabContent<
  'secondaryMarketQuotes',
  'live',
  SecondaryMarketFormTabContent<LiveInputForm>
>;

export type SecondaryPostEventsCpOrderPersistedTabContent = SecondaryMarketPersistedTabContent<
  'secondaryMarketPostEvents',
  'cpOrder',
  SecondaryMarketCpOrderFormTabContent
>;
export type SecondaryPostEventsLivePersistedTabContent = SecondaryMarketPersistedTabContent<
  'secondaryMarketPostEvents',
  'live',
  SecondaryMarketFormTabContent<LiveInputForm>
>;

export type SecondaryQuotesLimitOrderFormPersistedTabContent = SecondaryMarketPersistedTabContent<
  'secondaryMarketQuotes',
  'limitOrder',
  SecondaryMarketFormTabContent<LimitOrderInputForm>
>;

export type SecondaryPostEventsLimitOrderPersistedTabContent = SecondaryMarketPersistedTabContent<
  'secondaryMarketPostEvents',
  'limitOrder',
  SecondaryMarketFormTabContent<LimitOrderInputForm>
>;

export type SecondaryQuotesPersistedTabContent =
  | SecondaryQuotesRtFormPersistedTabContent
  | SecondaryQuotesCpOrderPersistedTabContent
  | SecondaryQuotesLiveFormPersistedTabContent
  | SecondaryPostEventsCpOrderPersistedTabContent
  | SecondaryPostEventsLivePersistedTabContent
  | SecondaryQuotesLimitOrderFormPersistedTabContent
  | SecondaryPostEventsLimitOrderPersistedTabContent
  | SecondaryQuotesDlpOrderPersistedTabContent;

export function isSecondaryMarketQuotesRtFormPersistedTabContent(
  tabContent: PersistedTabContent,
): tabContent is SecondaryQuotesRtFormPersistedTabContent {
  return tabContent.workspace === 'secondaryMarketQuotes' && tabContent.tabType === 'realtimeQuote';
}

export function isSecondaryMarketQuotesCpOrderPersistedTabContent(
  tabContent: PersistedTabContent,
): tabContent is SecondaryQuotesCpOrderPersistedTabContent {
  return tabContent.workspace === 'secondaryMarketQuotes' && tabContent.tabType === 'cpOrder';
}
export function isSecondaryPersistedTabContent<F extends Form>(
  tabContent: PersistedTabContent,
): tabContent is SecondaryQuotesPersistedTabContent & SecondaryMarketFormTabContent<F> {
  return tabContent.workspace === 'secondaryMarketQuotes' || tabContent.workspace === 'secondaryMarketPostEvents';
}

export function isSecondaryMarketQuotesLivePersistedTabContent(
  tabContent: PersistedTabContent,
): tabContent is SecondaryQuotesLiveFormPersistedTabContent {
  return tabContent.workspace === 'secondaryMarketQuotes' && tabContent.tabType === 'live';
}
export function isSecondaryMarketQuotesLimitOrderPersistedTabContent(
  tabContent: PersistedTabContent,
): tabContent is SecondaryQuotesLimitOrderFormPersistedTabContent {
  return tabContent.workspace === 'secondaryMarketQuotes' && tabContent.tabType === 'limitOrder';
}

export function isSecondaryPostEventsCpOrderPersistedTabContent(
  tabContent: PersistedTabContent,
): tabContent is SecondaryPostEventsCpOrderPersistedTabContent {
  return tabContent.workspace === 'secondaryMarketPostEvents' && tabContent.tabType === 'cpOrder';
}

export function isSecondaryMarketQuotesDlpOrderPersistedTabContent(
  tabContent: PersistedTabContent,
): tabContent is SecondaryQuotesRtFormPersistedTabContent {
  return tabContent.workspace === 'secondaryMarketQuotes' && tabContent.tabType === 'dlpOrder';
}
